import { z } from 'zod';
import { list } from 'lib/types/list';
import { isLiveBlogLive } from 'lib/liveBlog';
import { BADGES } from 'components/BadgeLabel/constants';

/** @typedef {"BREAKING" | "LIVE" | "EXCLUSIVE"} Badge */

export const badgeSchema = z.enum([BADGES.LIVE, BADGES.EXCLUSIVE, BADGES.BREAKING]).optional();

export const SubType = z.enum([
  'COMMERCE',
  'FEATURED',
  'blog',
  'externalLink',
  'liveBlog',
  'photo',
  'post',
  'slideshow',
  'spread',
  'video',
  'videoSpread',
]);

/** @typedef {z.infer<typeof SubType>} SubType */

/**
 * Takes a string representing a badge.  If it's one of the supported
 * badges, the corresponding badge is returned.
 *
 * @param {string | undefined} maybeBadge
 * @returns {Badge | undefined}
 */
export function convertToBadge(maybeBadge) {
  const parsedBadge = badgeSchema.safeParse(maybeBadge?.toUpperCase());
  return parsedBadge.success ? parsedBadge.data : undefined;
}

/**
 * Return the badge to display for the specified article
 *
 * @param {{
 *   taxonomy: {
 *     badge: {
 *       name: string;
 *     } | null;
 *   } | null;
 *   subType: SubType;
 *   eventDateTime: {
 *     endsAt: string | null;
 *   };
 *   flag: string | null;
 *   cards: {
 *     items: Array<{
 *       date: {
 *         publishedAt: string | null;
 *       };
 *     } | null> | null
 *   } | null;
 *   breakingNews: boolean | null;
 * }} article - The article object to check.  The type of this article represents the fields that need to be present for the badge to be determined correctly
 * @returns {Badge | undefined}
 */
export function getBadge(article) {
  const latestCardDate = list(article.cards?.items)
    .map((c) => c.date.publishedAt)
    .reduce((a, b) => ((a ?? 0) > (b ?? 0) ? a : b), null);

  const isLive = article.subType === 'liveBlog'
    && isLiveBlogLive({
      latestDate: latestCardDate,
      flag: article.flag,
      eventDateTimeEndsAt: article.eventDateTime.endsAt,
    });

  return isLive ? BADGES.LIVE : undefined;
}
